import { makeAutoObservable } from "mobx";
import {
  DbotEventDispatcher,
  DecisionContext,
  deleteAttachmentEvent,
  UploadingAttachment,
} from "@/dbot/common/commonDomain";
import { DecisionType } from "@/common/decisions";
import { LocalDateFieldModel } from "@/common/commonForms";
import { prepDecisionAttachmentsDropzone } from "@/dbot/attachments";
import { UploadFilesPanelProps } from "@/components-v2/UploadFilesPanel";
import { TMessage } from "@/i18n/tt";
import { ProjectDecisionAttachment } from "@/fileUpload/fileUploadDomain";

export type InterimBalanceSheet = {
  type: DecisionType.interimBalanceSheet;
  balanceSheetDate?: string;
  hasAuditor: boolean;
  balanceSheetAttachmentId?: string;
  auditorConfirmationAttachmentId?: string;
};

export type AttachmentQuestionKey = "balanceSheet" | "auditorConfirmation";

export type AttachmentQuestion = {
  attachment?: ProjectDecisionAttachment;
  uploads: UploadingAttachment[];
  dropzone?: UploadFilesPanelProps;
  isDeleting: boolean;
  onOpen: () => void;
  onDelete?: () => void;
};

function uploadsFor(
  form: InterimBalanceSheetForm,
  key: AttachmentQuestionKey,
): UploadingAttachment[] {
  const { state, decisionId } = form.context;
  return state.type === "uploading-attachments" &&
    state.reference === decisionId &&
    form.uploadingQuestion === key
    ? state.uploads
    : [];
}

function attachmentQuestion(
  form: InterimBalanceSheetForm,
  key: AttachmentQuestionKey,
  attachmentId: string | undefined,
  onUploaded: (attachmentId: string) => void,
): AttachmentQuestion {
  const { context, dispatch } = form;
  const attachment = context.attachments.find((a) => a.id === attachmentId);
  const uploads = uploadsFor(form, key);
  const dropzone =
    attachment || uploads.length > 0 || context.isLocked
      ? undefined
      : prepDecisionAttachmentsDropzone(context, context.decisionId, dispatch, (uploaded) => {
          const [first] = uploaded;
          if (first) {
            onUploaded(first.id);
          }
        });
  return {
    attachment,
    uploads,
    dropzone: dropzone && {
      ...dropzone,
      multiple: false,
      onUpload: (files, fileUploadConfig) => {
        form.setUploadingQuestion(key);
        const events = dropzone.onUpload(files, fileUploadConfig);
        if (events) {
          dispatch(events);
        }
      },
    },
    isDeleting:
      context.state.type === "deleting-attachment" && context.state.attachmentId === attachmentId,
    onOpen: () => {
      if (attachment) {
        dispatch({ type: "dbot/open-attachment", attachmentId: attachment.id });
      }
    },
    onDelete:
      attachment && !context.isLocked
        ? () =>
            dispatch(
              deleteAttachmentEvent({
                attachmentId: attachment.id,
                source: context.decisionId,
                reference: context.decisionId,
              }),
            )
        : undefined,
  };
}

export class InterimBalanceSheetForm {
  readonly type = DecisionType.interimBalanceSheet;
  balanceSheetDate: LocalDateFieldModel;
  balanceSheetAttachmentId?: string;
  auditorConfirmationAttachmentId?: string;
  balanceSheetValidationError?: TMessage;
  uploadingQuestion?: AttachmentQuestionKey;

  constructor(
    readonly dispatch: DbotEventDispatcher,
    readonly context: DecisionContext<InterimBalanceSheet>,
    public data?: InterimBalanceSheet,
  ) {
    this.balanceSheetDate = new LocalDateFieldModel({
      value: data?.balanceSheetDate,
      required: true,
    });
    this.balanceSheetAttachmentId = data?.balanceSheetAttachmentId;
    this.auditorConfirmationAttachmentId = data?.auditorConfirmationAttachmentId;
    makeAutoObservable(this, { type: false, dispatch: false, context: false }, { autoBind: true });
  }

  get hasAuditor(): boolean {
    return Boolean(this.context.initialState.currentAuditor);
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get balanceSheet(): AttachmentQuestion {
    return attachmentQuestion(
      this,
      "balanceSheet",
      this.balanceSheetAttachmentId,
      this.setBalanceSheetAttachmentId,
    );
  }

  get auditorConfirmation(): AttachmentQuestion {
    return attachmentQuestion(
      this,
      "auditorConfirmation",
      this.auditorConfirmationAttachmentId,
      this.setAuditorConfirmationAttachmentId,
    );
  }

  get hasBalanceSheet(): boolean {
    return Boolean(this.balanceSheet.attachment);
  }

  get isValid(): boolean {
    return this.balanceSheetDate.isValid && this.hasBalanceSheet;
  }

  setUploadingQuestion(key: AttachmentQuestionKey) {
    this.uploadingQuestion = key;
  }

  setBalanceSheetAttachmentId(attachmentId: string) {
    this.balanceSheetAttachmentId = attachmentId;
    this.balanceSheetValidationError = undefined;
  }

  setAuditorConfirmationAttachmentId(attachmentId: string) {
    this.auditorConfirmationAttachmentId = attachmentId;
  }

  validate(): boolean {
    this.balanceSheetDate.validate();
    this.balanceSheetValidationError = this.hasBalanceSheet
      ? undefined
      : { key: "decisions.interimBalanceSheet.balanceSheetUpload.required" };
    return this.isValid;
  }
}
